import { PRINTED_FILE_v2_LINK } from '../../../constants';

// Flag to prevent multiple simultaneous print operations
export const printingState = {
  isPrinting: false,
};

/**
 * Safari (desktop and iOS) does not reliably print PDFs loaded into a hidden
 * <iframe>.
 * The working fallback is to hand the URL to the native PDF viewer in a new
 * tab so the user can use its print dialog, which honors the PDF's own
 * page size.
 */
export function isSafariBrowser() {
  const ua = navigator.userAgent || '';
  return /Safari/i.test(ua) && !/Chrome|CriOS|Firefox/i.test(ua);
}

function openPdfInNewTab(pdfApiUrl) {
  const newWindow = window.open(pdfApiUrl, '_blank', 'noopener,noreferrer');
  if (newWindow) {
    newWindow.focus();
    return true;
  }
  return false;
}

async function uploaderPdfToIframe(pk) {
  // Prevent multiple simultaneous calls
  if (printingState.isPrinting) {
    return;
  }

  printingState.isPrinting = true;

  const pdfApiUrl = `${PRINTED_FILE_v2_LINK}${pk}`;

  if (isSafariBrowser()) {
    const opened = openPdfInNewTab(pdfApiUrl);
    if (!opened) {
      window.location.href = pdfApiUrl;
    }
    printingState.isPrinting = false;
    return;
  }

  const existing = document.getElementById('pdf-print-frame');
  if (existing) {
    existing.remove();
  }

  const iframe = document.createElement('iframe');
  iframe.id = 'pdf-print-frame';
  // Keep the iframe off-screen but give it a real, paper-sized box so the
  // browser print pipeline picks up the PDF's intended page geometry instead
  // of shrinking everything to a 1x1 pixel frame.
  iframe.style.position = 'fixed';
  iframe.style.top = '-10000px';
  iframe.style.left = '-10000px';
  iframe.style.width = '210mm';
  iframe.style.height = '297mm';
  iframe.style.border = '0';
  iframe.style.opacity = '0';
  iframe.style.pointerEvents = 'none';
  iframe.style.zIndex = '-1';
  iframe.setAttribute('aria-hidden', 'true');

  iframe.src = pdfApiUrl;

  iframe.onload = () => {
    // Start polling to check if PDF is ready
    let attempts = 0;
    const maxAttempts = 20; // Try for up to 10 seconds (20 * 500ms)

    const checkAndPrint = () => {
      attempts++;
      const iframeWindow = iframe.contentWindow;

      if (!iframeWindow) {
        printingState.isPrinting = false;
        return;
      }

      try {
        const iframeDoc = iframe.contentDocument || iframeWindow.document;

        // Check if PDF is loaded by looking for embed/object elements or content
        const hasEmbed = iframeDoc?.querySelector(
          'embed[type="application/pdf"]',
        );
        const hasObject = iframeDoc?.querySelector(
          'object[type="application/pdf"]',
        );
        const hasContent =
          iframeDoc?.body &&
          (iframeDoc.body.innerHTML.length > 0 ||
            iframeDoc.body.children.length > 0 ||
            hasEmbed ||
            hasObject);

        if (hasContent) {
          // Note: Print dialog may not appear if DevTools are open and focused
          // This is expected browser behavior in Chrome/Edge
          iframeWindow.focus();
          iframeWindow.print();
          setTimeout(() => {
            printingState.isPrinting = false;
          }, 1000);
        } else if (attempts < maxAttempts) {
          // PDF not ready yet, retry after 500ms
          setTimeout(checkAndPrint, 500);
        } else {
          // Max attempts reached, try printing anyway
          console.warn(
            '[uploaderPdfToIframe] Max attempts reached, attempting print anyway',
          );
          try {
            iframeWindow.focus();
            iframeWindow.print();
            setTimeout(() => {
              printingState.isPrinting = false;
            }, 1000);
          } catch (printError) {
            console.error(
              '[uploaderPdfToIframe] Error during print:',
              printError,
            );
            printingState.isPrinting = false;
          }
        }
      } catch (error) {
        // Cross-origin or other error - can't check content, try printing anyway
        try {
          iframeWindow.focus();
          iframeWindow.print();
          setTimeout(() => {
            printingState.isPrinting = false;
          }, 1000);
        } catch (printError) {
          console.error(
            '[uploaderPdfToIframe] Error during print:',
            printError,
          );
          printingState.isPrinting = false;
        }
      }
    };

    // Start checking after initial delay
    setTimeout(checkAndPrint, 100);
  };

  iframe.onerror = () => {
    console.error('[uploaderPdfToIframe] Failed to load iframe');
    printingState.isPrinting = false;
  };

  document.body.appendChild(iframe);
}

export default uploaderPdfToIframe;
